import React, {
  FC,
  MutableRefObject,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import {
  InputActionMeta,
  MultiValue,
  SingleValue,
} from "react-select";
import AsyncSelect from "react-select/async";
import makeAnimated from "react-select/animated";
import Rates, { Rate, RateCategory, RateType } from "../common/Rates";
import {
  Departure,
  Schedule,
  ScheduleProvider,
} from "../../../interfaces/schedule.interface";
import { ScheduleStatus } from "../../../enums/schedule.enum";
import { getProviders } from "../../../utils/rest-api/providers";
import { DateObject } from "react-multi-date-picker";
import {
  addSchedule,
  getSchedule,
  updateSchedule,
} from "../../../utils/rest-api/schedules";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel";
import { parseErrorToObject } from "../../../utils/parseErrorToObject";
import { getLocations } from "../../../utils/rest-api/locations";
import { Location } from "../../../interfaces/location.interface";
import classNames from "classnames";
import { ExtendedDatePicker } from "../../common/ExtendedDatePicker";
import { ExtendedSelect } from "../../common/ExtendedSelect";
import { useApp } from "../../../contexts/AppContext";
import dayjs from "dayjs";
import { Trip, TripResponse } from "../../../interfaces/trip.interface";
import { getTrips } from "../../../utils/rest-api/trips";
import { AxiosResponse } from "axios";
import {
  addSeperator,
  removeNonNumeric,
} from "../../../utils/currencyValidator";

const statuses = [
  ScheduleStatus.PENDING,
  ScheduleStatus.OPEN,
  ScheduleStatus.CLOSED,
  ScheduleStatus.EXPIRED,
  ScheduleStatus.FULL,
];

export interface ScheduleFormProps extends PropsWithChildren {
  className?: string;
  scheduleId?: string;
}

const ScheduleForm: FC<ScheduleFormProps> = (props) => {
  const [schedule, setSchedule] = useState<Schedule>({
    _id: "",
    provider: {
      _id: "",
      name: "",
    },
    origin: "",
    destination: "",
    rates: [],
    status: ScheduleStatus.PENDING,
    createdAt: new Date(),
    updatedAt: new Date(),
    by: "",
    cancellationFee: undefined,
    taxAmount: null,
    surchargeAmount: null,
    returnTaxAmount: null,
    returnSurchargeAmount: null,
    restrictedReturnTaxAmount: null,
    restrictedReturnSurchargeAmount: null,
    restrictedReturnProviders: [],
  });

  const [states, setStates] = useState<Location[]>([]);

  const [invalidScheduleProps, setInvalidScheduleProps] = useState({
    provider: "",
    origin: "",
    destination: "",
    rates: [],
    status: "",
    departure: "",
    cancellationFee: "",
    taxAmount: "",
    surchargeAmount: "",
    returnTaxAmount: "",
    returnSurchargeAmount: "",
    restrictedReturnProviders: "",
    restrictedReturnTaxAmount: "",
    restrictedReturnSurchargeAmount: "",
  });

  const navigate = useNavigate();

  const onDepartureDateChange = (dates: DateObject | DateObject[] | null) => {
    setInvalidScheduleProps({ ...invalidScheduleProps, departure: "" });
    if (dates == null) {
      setDepartures([]);
    } else if (Array.isArray(dates)) {
      let departures: Departure[] = dates.map((dt) => {
        let date = dt.setSecond(0).toDate();
        let departure = dayjs(date).format("YYYY-MM-DDTHH:mm");
        return {
          departure: departure,
        };
      });
      setDepartures(departures);
    } else {
      let date = dates.toDate();
      let departure = dayjs(date).format("YYYY-MM-DDTHH:mm");
      setDepartures([{ departure: departure }]);
    }
  };
  const [departures, setDepartures] = useState<Array<Departure>>([]);

  const defaultAgentRates: Array<Rate> = [
    {
      category: RateCategory.ADULT,
      basePrice: undefined,
      margin: 0,
      twoWay: false,
      type: RateType.USER,
      isForeigner: false,
    },
    {
      category: RateCategory.CHILD,
      basePrice: undefined,
      margin: 0,
      twoWay: false,
      type: RateType.USER,
      isForeigner: false,
    },
    {
      category: RateCategory.INFANT,
      basePrice: undefined,
      margin: 0,
      twoWay: false,
      type: RateType.USER,
      isForeigner: false,
    },
  ];

  const [options, setOptions] = useState({
    diferForeigner: false,
    hasTwoWay: false,
    hasAgentPrice: false,
    hasRestrictedReturn: false,
  });

  type PriceWarningItem = {
    category: RateCategory;
    isForeigner: boolean;
    rateType: RateType;
    generalTotal: number;
    restrictedTotal: number;
  };
  const [priceWarning, setPriceWarning] = useState<PriceWarningItem[] | null>(null);

  const onRateChanges = (
    changedRate: Rate,
    name: "basePrice" | "margin",
    value: string,
  ) => {
    const scheduleToSet = { ...schedule };
    const rateIndex = Number(
      scheduleToSet.rates?.findIndex((rate) => rate == changedRate),
    );

    const IntValue = value === "" ? undefined : parseInt(value);
    changedRate[name] = IntValue;
    scheduleToSet.rates![rateIndex] = { ...changedRate };

    const invalidSchedulePropsToSet = { ...invalidScheduleProps };
    const invalidRateToSet: any = invalidSchedulePropsToSet?.rates?.[rateIndex];

    if (invalidRateToSet) {
      invalidRateToSet.basePrice = "";
      setInvalidScheduleProps(invalidSchedulePropsToSet);
    }
    setSchedule(scheduleToSet);
  };

  const { showLoader, hideLoader } = useApp();

  useEffect(() => {
    const promises = [];
    promises.push(
      getLocations()
        .then((res) => {
          setStates(res.data.data);
        })
        .catch(() => {}),
    );
    promises.push(
      getProviders(undefined, true, 1, 5)
        .then((res) => {
          setProviders(res.data.data);
        })
        .catch(() => {}),
    );

    if (props.scheduleId) {
      promises.push(
        getSchedule(props.scheduleId).then((res) => {
          const data = res.data.data;
          const hasTwoWay =
            data.rates.find((rate: Rate) => rate.twoWay && !(rate.isRestricted ?? false)) !== undefined;
          const hasAgentPrice =
            data.rates.find((rate: Rate) => rate.type == RateType.AGENT) !==
            undefined;
          const diferForeigner =
            data.rates.find((rate: Rate) => rate.isForeigner) !== undefined;
          data.origin = data.origin._id;
          data.destination = data.destination._id;
          data.taxAmount = data.taxAmount ?? null;
          data.surchargeAmount = data.surchargeAmount ?? null;
          data.returnTaxAmount = data.returnTaxAmount ?? null;
          data.returnSurchargeAmount = data.returnSurchargeAmount ?? null;
          data.restrictedReturnTaxAmount = data.restrictedReturnTaxAmount ?? null;
          data.restrictedReturnSurchargeAmount = data.restrictedReturnSurchargeAmount ?? null;
          data.restrictedReturnProviders = data.restrictedReturnProviders ?? [];
          const hasRestrictedReturn = data.rates.some(
            (r: Rate) => r.twoWay === true && r.isRestricted === true
          );
          setOptions({
            hasTwoWay: hasTwoWay,
            diferForeigner: diferForeigner,
            hasAgentPrice: hasAgentPrice,
            hasRestrictedReturn: hasRestrictedReturn,
          });

          let date = new Date(data.departure);
          let departure = dayjs(date).format("YYYY-MM-DDTHH:mm");
          setDepartures([{ departure: departure }]);
          setSchedule(data);
        }),
      );
    } else {
      setOptions({
        hasTwoWay: false,
        diferForeigner: false,
        hasAgentPrice: false,
        hasRestrictedReturn: false,
      });
      setSchedule({ ...schedule, rates: defaultAgentRates });
    }
    showLoader();
    Promise.all(promises).finally(() => hideLoader());
  }, []);

  const [providers, setProviders] = useState<ScheduleProvider[]>([]);

  let providerTimeoutId: MutableRefObject<NodeJS.Timeout | undefined> =
    useRef(undefined);
  const onProviderInputChange = (value: string, action: InputActionMeta) => {
    setInvalidScheduleProps({ ...invalidScheduleProps, provider: "" });
    if (providerTimeoutId && providerTimeoutId.current) {
      clearTimeout(providerTimeoutId.current);
      providerTimeoutId.current = undefined;
    }
    if (action.action == "input-change") {
      providerTimeoutId.current = setTimeout(() => {
        getProviders(value, true, 1, 5)
          .then((res) => {
            setProviders(res.data.data);
          })
          .catch(() => {});
        providerTimeoutId.current = undefined;
      }, 1000);
    }
  };

  const handleError = (err: any) => {
    if (err.response?.data) {
      if (typeof err.response.data?.message == "object") {
        const data: any = parseErrorToObject(err.response.data.message);
        // POST nests errors under "schedule.*"; PATCH uses flat keys
        const fieldErrors = data.schedule ?? data;
        setInvalidScheduleProps({
          ...invalidScheduleProps,
          ...fieldErrors,
          departure: data.departures,
        });
      } else {
        toast.error(err.response.data?.message ?? err.response.data?.error);
      }
    } else {
      toast.error("Please check your network connection");
    }
  };

  const executeSave = () => {
    setPriceWarning(null);
    if (props.scheduleId) {
      showLoader();
      updateSchedule(props.scheduleId, {
        provider: (schedule.provider as ScheduleProvider)._id,
        origin: schedule.origin,
        destination: schedule.destination,
        rates: schedule.rates,
        cancellationFee: schedule.cancellationFee,
        status: schedule.status,
        taxAmount: schedule.taxAmount,
        surchargeAmount: schedule.surchargeAmount,
        returnTaxAmount: schedule.returnTaxAmount,
        returnSurchargeAmount: schedule.returnSurchargeAmount,
        restrictedReturnTaxAmount: options.hasRestrictedReturn ? (schedule.restrictedReturnTaxAmount ?? null) : null,
        restrictedReturnSurchargeAmount: options.hasRestrictedReturn ? (schedule.restrictedReturnSurchargeAmount ?? null) : null,
        restrictedReturnProviders: (options.hasRestrictedReturn ? (schedule.restrictedReturnProviders ?? []).map((p) => p._id!) : []) as any,
      })
        .then(() => {
          toast.success("Schedules successfully updated");
          navigate("/schedules");
        })
        .catch((err) => {
          handleError(err);
        })
        .finally(() => hideLoader());
    } else {
      showLoader();
      addSchedule({
        departures: departures,
        schedule: {
          provider: (schedule.provider as ScheduleProvider)._id,
          origin: schedule.origin,
          destination: schedule.destination,
          rates: schedule.rates,
          cancellationFee: schedule.cancellationFee,
          status: schedule.status,
          taxAmount: schedule.taxAmount,
          surchargeAmount: schedule.surchargeAmount,
          returnTaxAmount: schedule.returnTaxAmount,
          returnSurchargeAmount: schedule.returnSurchargeAmount,
          restrictedReturnTaxAmount: options.hasRestrictedReturn ? (schedule.restrictedReturnTaxAmount ?? null) : null,
          restrictedReturnSurchargeAmount: options.hasRestrictedReturn ? (schedule.restrictedReturnSurchargeAmount ?? null) : null,
          restrictedReturnProviders: (options.hasRestrictedReturn ? (schedule.restrictedReturnProviders ?? []).map((p) => p._id!) : []) as any,
        },
      })
        .then(() => {
          toast.success("Schedules successfully added");
          // Stays on this form to add more departures; if the admin later navigates
          // back to the list, show it fresh instead of the filters from before this add.
          sessionStorage.setItem("scheduleListShouldReset", "1");
          setTripProps({
            show: false,
            date: undefined,
            errorMessage: undefined,
            trip: undefined,
            tempTrip: undefined,
          });
          setSchedule({
            ...schedule,
            provider: "",
            origin: "",
            destination: "",
          });
          setDepartures([]);
        })
        .catch((err) => {
          handleError(err);
        })
        .finally(() => hideLoader());
    }
  };

  const onScheduleSave = () => {
    const provider = schedule.provider as ScheduleProvider;
    const fieldErrors = {
      provider: provider?._id ? "" : "Required",
      origin: schedule.origin ? "" : "Required",
      destination: schedule.destination ? "" : "Required",
      departure:
        !props.scheduleId && departures.length === 0 ? "Required" : "",
      cancellationFee:
        schedule.cancellationFee === undefined || schedule.cancellationFee === ""
          ? "Required"
          : "",
      taxAmount:
        schedule.taxAmount !== null && schedule.taxAmount === undefined
          ? "Required"
          : "",
      surchargeAmount:
        schedule.surchargeAmount !== null && schedule.surchargeAmount === undefined
          ? "Required"
          : "",
      returnTaxAmount:
        options.hasTwoWay &&
        schedule.returnTaxAmount !== null &&
        schedule.returnTaxAmount === undefined
          ? "Required"
          : "",
      returnSurchargeAmount:
        options.hasTwoWay &&
        schedule.returnSurchargeAmount !== null &&
        schedule.returnSurchargeAmount === undefined
          ? "Required"
          : "",
      restrictedReturnProviders:
        options.hasRestrictedReturn && !(schedule.restrictedReturnProviders?.length)
          ? "Required"
          : "",
      restrictedReturnTaxAmount:
        options.hasRestrictedReturn &&
        schedule.restrictedReturnTaxAmount !== null &&
        schedule.restrictedReturnTaxAmount === undefined
          ? "Required"
          : "",
      restrictedReturnSurchargeAmount:
        options.hasRestrictedReturn &&
        schedule.restrictedReturnSurchargeAmount !== null &&
        schedule.restrictedReturnSurchargeAmount === undefined
          ? "Required"
          : "",
    };
    const rateErrors = (schedule.rates ?? []).map((rate) => {
      const price = rate.basePrice;
      if (price === undefined || price === null) return { basePrice: "Required" };
      const num = parseInt(price.toString());
      if (Number.isNaN(num)) return { basePrice: "Invalid price" };
      return { basePrice: "" };
    });
    const hasFieldErrors = Object.values(fieldErrors).some((e) => e !== "");
    const hasRateErrors = rateErrors.some((e) => e.basePrice !== "");
    if (hasFieldErrors || hasRateErrors) {
      setInvalidScheduleProps({
        ...invalidScheduleProps,
        ...fieldErrors,
        rates: rateErrors as any,
      });
      toast.error("Please fill in all required fields before saving");
      return;
    }

    if (options.hasTwoWay && options.hasRestrictedReturn) {
      const rates = schedule.rates ?? [];
      const outboundTaxSurcharge =
        (schedule.taxAmount ?? 0) + (schedule.surchargeAmount ?? 0);
      const returnTaxSurcharge =
        (schedule.returnTaxAmount ?? 0) + (schedule.returnSurchargeAmount ?? 0);
      const restrictedReturnTaxSurcharge =
        (schedule.restrictedReturnTaxAmount ?? 0) +
        (schedule.restrictedReturnSurchargeAmount ?? 0);
      const hasForeigner = rates.some((r) => r.isForeigner);
      const hasAgent = rates.some((r) => r.type === RateType.AGENT);
      const nationalities = hasForeigner ? [false, true] : [false];
      const rateTypes = hasAgent ? [RateType.USER, RateType.AGENT] : [RateType.USER];
      const categories = [RateCategory.ADULT, RateCategory.CHILD, RateCategory.INFANT];
      // At equal totals, restricted is cheaper only when general charges nothing (both null)
      // and restricted charges something (non-null). Every other equal-total combination is flagged:
      //   both included → not cheaper
      //   general included, restricted not → not cheaper
      //   both not included → not cheaper
      //   general not included, restricted included → cheaper (advantage)
      const generalHasCharges =
        typeof schedule.returnTaxAmount === "number" ||
        typeof schedule.returnSurchargeAmount === "number";
      const restrictedHasCharges =
        typeof schedule.restrictedReturnTaxAmount === "number" ||
        typeof schedule.restrictedReturnSurchargeAmount === "number";
      const restrictedHasIncludedAdvantage = !generalHasCharges && restrictedHasCharges;
      const failures: PriceWarningItem[] = [];
      for (const rateType of rateTypes) {
        for (const category of categories) {
          for (const isForeigner of nationalities) {
            const generalRate = rates.find(
              (r) =>
                r.twoWay &&
                !(r.isRestricted ?? false) &&
                r.category === category &&
                r.isForeigner === isForeigner &&
                r.type === rateType
            );
            const restrictedRate = rates.find(
              (r) =>
                r.twoWay &&
                (r.isRestricted ?? false) &&
                r.category === category &&
                r.isForeigner === isForeigner &&
                r.type === rateType
            );
            if (
              generalRate?.basePrice === undefined ||
              restrictedRate?.basePrice === undefined
            )
              continue;
            const generalBase = parseInt(generalRate.basePrice.toString());
            const restrictedBase = parseInt(restrictedRate.basePrice.toString());
            if (isNaN(generalBase) || isNaN(restrictedBase)) continue;
            const generalTotal = generalBase + outboundTaxSurcharge + returnTaxSurcharge;
            const restrictedTotal = restrictedBase + outboundTaxSurcharge + restrictedReturnTaxSurcharge;
            const notCheaper =
              restrictedTotal > generalTotal ||
              (restrictedTotal === generalTotal && !restrictedHasIncludedAdvantage);
            if (notCheaper) {
              failures.push({ category, isForeigner, rateType, generalTotal, restrictedTotal });
            }
          }
        }
      }
      if (failures.length > 0) {
        setPriceWarning(failures);
        return;
      }
    }

    executeSave();
  };

  const onScheduleFormChange = (name: string, value: any) => {
    setSchedule({ ...schedule, [name]: value });
    setInvalidScheduleProps({ ...invalidScheduleProps, [name]: "" });
  };

  const OnSwitchChanged = (type: string) => {
    switch (type) {
      case "difer-foreigner":
        const hasForeigner = !options.diferForeigner;
        if (!hasForeigner) {
          const rates = schedule.rates!.filter((rate) => {
            return rate.isForeigner === false;
          });
          setSchedule({ ...schedule, rates: rates });
        } else {
          const rates = schedule.rates!.filter((rate) => {
            return rate.isForeigner === false;
          });
          const ratesToAdd = [...rates];
          for (let i = 0; i < rates.length; i++) {
            ratesToAdd.push({ ...rates[i], isForeigner: true });
          }
          setSchedule({ ...schedule, rates: ratesToAdd });
        }
        setOptions({ ...options, diferForeigner: !options.diferForeigner });
        break;
      case "has-two-way":
        const hasTwoWay = !options.hasTwoWay;
        if (!hasTwoWay) {
          // Remove round-trip rates but preserve restricted return rates
          const rates = schedule.rates!.filter(
            (rate) => !rate.twoWay || (rate.isRestricted ?? false)
          );
          setSchedule({ ...schedule, rates, returnTaxAmount: null, returnSurchargeAmount: null });
          setOptions({ ...options, hasTwoWay: false });
        } else {
          const oneWayRates = schedule.rates!.filter((rate) => !rate.twoWay);
          const existingRestrictedRates = schedule.rates!.filter(
            (rate) => rate.isRestricted ?? false
          );
          const roundTripRates = oneWayRates.map((rate) => ({
            ...rate,
            twoWay: true,
            isRestricted: false,
          }));
          setSchedule({
            ...schedule,
            rates: [...oneWayRates, ...roundTripRates, ...existingRestrictedRates],
          });
          setOptions({ ...options, hasTwoWay: true });
        }
        break;
      case "has-agent-price":
        const hasAgentPrice = !options.hasAgentPrice;
        if (!hasAgentPrice) {
          const rates = schedule.rates!.filter((rate) => {
            return rate.type == RateType.USER;
          });
          setSchedule({ ...schedule, rates: rates });
        } else {
          const rates = schedule.rates!.filter((rate) => {
            return rate.type == RateType.USER;
          });
          const ratesToAdd = [...rates];
          for (let i = 0; i < rates.length; i++) {
            ratesToAdd.push({ ...rates[i], type: RateType.AGENT });
          }
          setSchedule({ ...schedule, rates: ratesToAdd });
        }
        setOptions({ ...options, hasAgentPrice: !options.hasAgentPrice });
        break;
      case "has-restricted-return":
        const hasRestrictedReturn = !options.hasRestrictedReturn;
        if (!hasRestrictedReturn) {
          const rates = schedule.rates!.filter(
            (rate) => !(rate.isRestricted ?? false)
          );
          setSchedule({
            ...schedule,
            rates,
            restrictedReturnTaxAmount: null,
            restrictedReturnSurchargeAmount: null,
            restrictedReturnProviders: [],
          });
        } else {
          // Derive from round-trip rates if available, otherwise from one-way rates
          const twoWayBaseRates = schedule.rates!.filter(
            (rate) => rate.twoWay && !(rate.isRestricted ?? false)
          );
          const baseForRestricted =
            twoWayBaseRates.length > 0
              ? twoWayBaseRates
              : schedule.rates!.filter((rate) => !(rate.isRestricted ?? false));
          const restrictedRates = baseForRestricted.map((rate) => ({
            ...rate,
            twoWay: true,
            isRestricted: true,
          }));
          setSchedule({
            ...schedule,
            rates: [...schedule.rates!, ...restrictedRates],
          });
        }
        setOptions({ ...options, hasRestrictedReturn: !options.hasRestrictedReturn });
        break;
    }
  };

  interface TripProps {
    show: boolean;
    date?: DateObject;
    errorMessage?: string;
    trip?: Trip[];
    tempTrip?: Trip[];
  }

  const [tripProps, setTripProps] = useState<TripProps>({
    show: false,
    date: undefined,
    errorMessage: undefined,
    trip: undefined,
    tempTrip: undefined,
  });

  const onTripChange = (value: DateObject) => {
    setTripProps((prev) => ({ ...prev, date: value }));
  };

  const onTripSearch = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (tripProps.date) {
      setTripProps({
        show: true,
        date: tripProps.date,
        trip: tripProps.trip,
        tempTrip: undefined,
      });
    } else {
      setTripProps((prev) => ({
        ...prev,
        show: false,
        errorMessage: "Please Select Date",
      }));
    }
  };

  const onTripSelect = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setTripProps({
      show: false,
      date: tripProps.date,
      errorMessage: undefined,
      trip: tripProps.tempTrip,
      tempTrip: undefined,
    });

    if (tripProps.tempTrip) {
      setSchedule({
        ...schedule,
        provider: tripProps.tempTrip?.at(0)?.provider,
        origin: tripProps.tempTrip?.at(0)?.origin._id,
        destination: tripProps.tempTrip?.at(0)?.destination._id,
      });

      let departures: Departure[] = tripProps.tempTrip.map((dt) => {
        let date = new Date(Date.parse(`${dt.date} ${dt.time}`));
        let departure = dayjs(date).format("YYYY-MM-DDTHH:mm");

        return { departure: departure, tripId: dt.tripId };
      });

      setDepartures(departures);

      setInvalidScheduleProps({
        ...invalidScheduleProps,
        provider: "",
        origin: "",
        destination: "",
      });
    } else {
      setSchedule({
        ...schedule,
        provider: "",
        origin: "",
        destination: "",
      });

      setDepartures([]);

      setInvalidScheduleProps({
        ...invalidScheduleProps,
        provider: "",
        origin: "",
        destination: "",
      });
    }
  };

  const animatedComponents = makeAnimated();
  let abortController: AbortController | undefined = undefined;
  let terminalTimeoutId: NodeJS.Timeout | undefined = undefined;

  const loadOptions = (
    _inputValue: string,
    callback: (options: Trip[]) => void,
  ) => {
    if (terminalTimeoutId) {
      clearTimeout(terminalTimeoutId);
      terminalTimeoutId = undefined;
    }

    terminalTimeoutId = setTimeout(() => {
      abortController = new AbortController();
      if (!tripProps.date) return;
      getTrips({ date: tripProps.date.format("YYYY-MM-DD") }, abortController)
        .then((res: AxiosResponse<TripResponse>) => {
          if (abortController) callback(res.data.data);
        })
        .catch((e) => {
          toast.error(
            e.response?.data?.message ??
              e.response?.data?.error ??
              e.message ??
              "Internal server error, please try again in a few minutes",
          );
          callback([]);
        })
        .finally(() => {
          abortController = undefined;
        });
      terminalTimeoutId = undefined;
    }, 1000);
  };

  return (
    <div
      id="schedule-form-scroll-container"
      className={`ps-2 pe-2 pb-30 ${props.className}`}
    >
      <div className="mb-20">
        <span className="text-30 lh-14 fw-600">
          {props.scheduleId ? "Edit" : "Add"} Schedule
        </span>
      </div>
      <Form onSubmit={(e) => e.preventDefault()}>
        {!props.scheduleId && (
          <React.Fragment>
            {/* Select */}
            <InputGroup className="mb-2 d-flex flex-row gap-2">
              <Form.Group className="w-100">
                <Form.Label>Select Schedule from Terminal</Form.Label>
                <div className="d-flex flex-row gap-2">
                  <div className="w-50 w-sm-100">
                    <ExtendedDatePicker
                      format="DD/MM/YYYY"
                      minDate={new Date()}
                      value={tripProps.date}
                      onChange={onTripChange}
                      inputClass={`${
                        tripProps.errorMessage && `border-error-2`
                      }`}
                      containerClassName="datetimepicker_wrapper"
                      calendarPosition="bottom-center"
                      fixMainPosition={true}
                    />
                  </div>
                  <Button
                    onClick={onTripSearch}
                    variant="primary"
                    className="flex-grow-1 w-140"
                    type="submit"
                  >
                    Search
                  </Button>
                </div>

                <Form.Text className={`text-error-2`}>
                  {tripProps.errorMessage}
                </Form.Text>
              </Form.Group>
            </InputGroup>
          </React.Fragment>
        )}
        <hr />
        {/* Form */}
        <InputGroup className="mb-2 d-flex flex-row gap-2">
          <Form.Group className="w-50 w-sm-100">
            <Form.Label>Provider</Form.Label>
            <ExtendedSelect
              classNames={{
                control: ({ isFocused }) =>
                  classNames(isFocused && "react-select-focus"),
              }}
              aria-label="Origin"
              getOptionLabel={(provider: ScheduleProvider) => provider.name}
              getOptionValue={(provider: ScheduleProvider) => provider._id!}
              options={providers}
              value={schedule.provider as ScheduleProvider}
              onInputChange={onProviderInputChange}
              onChange={(
                provider: SingleValue<ScheduleProvider>,
              ) => {
                setSchedule({
                  ...schedule,
                  provider: {
                    _id: provider?._id!,
                    name: provider?.name!,
                  },
                });
                setInvalidScheduleProps({
                  ...invalidScheduleProps,
                  provider: "",
                });
              }}
              isDisabled={tripProps.trip ? true : false || !!props.scheduleId}
            />
            <Form.Text className={`text-error-2`}>
              {invalidScheduleProps.provider}
            </Form.Text>
          </Form.Group>
          <Form.Group className="flex-grow-1">
            <Form.Label>Status</Form.Label>
            <Form.Select
              className={invalidScheduleProps.status && `border-error-2`}
              aria-label="Status"
              style={{ height: "44px" }}
              value={schedule.status}
              onChange={(e) =>
                onScheduleFormChange("status", e.currentTarget.value)
              }
              // disabled={!!props.scheduleId}
            >
              {statuses.map((status) => {
                return (
                  <option
                    key={`status-${status}`}
                    disabled={
                      [ScheduleStatus.EXPIRED, ScheduleStatus.FULL].indexOf(
                        status,
                      ) >= 0
                    }
                    value={status}
                  >{`${status.charAt(0).toUpperCase()}${status.slice(
                    1,
                  )}`}</option>
                );
              })}
            </Form.Select>
            <Form.Text className={`text-error-2`}>
              {invalidScheduleProps.status}
            </Form.Text>
          </Form.Group>
        </InputGroup>
        <InputGroup className="mb-3 flex flex-row gap-2">
          <Form.Group
            className="flex-grow-1"
            style={{ width: "calc((100% - 10px) / 2)" }}
          >
            <Form.Label>Origin</Form.Label>
            <Form.Select
              className={invalidScheduleProps.origin && `border-error-2`}
              style={{ height: "44px" }}
              aria-label="Origin"
              value={schedule.origin}
              onChange={(e) =>
                onScheduleFormChange("origin", e.currentTarget.value)
              }
              disabled={tripProps.trip ? true : false || !!props.scheduleId}
            >
              <option hidden></option>
              {states
                .filter((state) => state.type == "origin")
                .map((state) => (
                  <option key={state._id} value={`${state._id}`}>
                    {`${
                      state.country.charAt(0).toUpperCase() +
                      state.country.slice(1)
                    }-${
                      state.port.charAt(0).toUpperCase() + state.port.slice(1)
                    }`}
                  </option>
                ))}
            </Form.Select>
            <Form.Text className={`text-error-2`}>
              {invalidScheduleProps.origin}
            </Form.Text>
          </Form.Group>
          <Form.Group
            className="flex-grow-1"
            style={{ width: "calc((100% - 10px) / 2)" }}
          >
            <Form.Label>Destination</Form.Label>
            <Form.Select
              className={invalidScheduleProps.origin && `border-error-2`}
              style={{ height: "44px" }}
              aria-label="Destination"
              value={schedule.destination}
              onChange={(e) =>
                onScheduleFormChange("destination", e.currentTarget.value)
              }
              disabled={tripProps.trip ? true : false || !!props.scheduleId}
            >
              <option hidden></option>
              {states
                .filter((state) => state.type == "destination")
                .map((state) => (
                  <option key={state._id} value={`${state._id}`}>
                    {`${
                      state.country.charAt(0).toUpperCase() +
                      state.country.slice(1)
                    }-${
                      state.port.charAt(0).toUpperCase() + state.port.slice(1)
                    }`}
                  </option>
                ))}
            </Form.Select>
            <Form.Text className={`text-error-2`}>
              {invalidScheduleProps.destination}
            </Form.Text>
          </Form.Group>
        </InputGroup>
        <InputGroup className="mb-3 flex flex-row">
          <Form.Group className="w-50 w-sm-100">
            <Form.Label>Departure</Form.Label>
            <React.Fragment>
              {props.scheduleId !== undefined ? (
                <ExtendedDatePicker
                  plugins={[<TimePicker position="bottom" hideSeconds />]}
                  format="DD/MM/YYYY HH:mm"
                  minDate={new Date()}
                  value={departures[0] && new Date(departures[0].departure)}
                  onChange={onDepartureDateChange}
                  inputClass={`${
                    invalidScheduleProps.departure && `border-error-2`
                  }`}
                  containerClassName="datetimepicker_wrapper"
                  calendarPosition="bottom-center"
                  fixMainPosition={true}
                  disabled={tripProps.trip ? true : false || !!props.scheduleId}
                />
              ) : (
                <ExtendedDatePicker
                  plugins={[
                    <TimePicker position="bottom" hideSeconds />,
                    <DatePanel markFocused position="right" />,
                  ]}
                  format="DD/MM/YYYY HH:mm"
                  multiple
                  value={departures.map((dt) => new Date(dt.departure))}
                  minDate={new Date()}
                  onChange={onDepartureDateChange}
                  inputClass={`datetimepicker ${
                    invalidScheduleProps.departure && `border-error-2`
                  }`}
                  containerClassName="datetimepicker_wrapper"
                  calendarPosition="bottom-center"
                  fixMainPosition={true}
                  disabled={tripProps.trip ? true : false}
                />
              )}
            </React.Fragment>
            <Form.Text className={`text-error-2`}>
              {invalidScheduleProps.departure}
            </Form.Text>
          </Form.Group>
        </InputGroup>
        <Form.Group style={{ marginBottom: "2.5rem" }}>
          <Form.Label>Cancellation Fee</Form.Label>
          <Form.Control
            type="text"
            style={{ width: "10rem" }}
            placeholder="Required"
            value={
              schedule.cancellationFee !== undefined &&
              schedule.cancellationFee !== ""
                ? addSeperator(schedule.cancellationFee.toString())
                : ""
            }
            className={invalidScheduleProps.cancellationFee && `border-error-2`}
            onChange={(e) =>
              onScheduleFormChange(
                "cancellationFee",
                Number.isNaN(parseInt(removeNonNumeric(e.currentTarget.value)))
                  ? ""
                  : parseInt(removeNonNumeric(e.currentTarget.value)),
              )
            }
          />
          <Form.Text className={`text-error-2`}>
            {invalidScheduleProps.cancellationFee}
          </Form.Text>
        </Form.Group>
        <div className="mb-3">
          <h5 className="text-decoration-underline mb-3">Tax & Surcharge</h5>
          <Form.Check
            type="switch"
            label="Outbound Tax"
            className="mb-2"
            checked={schedule.taxAmount !== null}
            onChange={() =>
              onScheduleFormChange(
                "taxAmount",
                schedule.taxAmount !== null ? null : undefined,
              )
            }
          />
          {schedule.taxAmount !== null && (
            <Form.Group className="mb-3" style={{ maxWidth: "10rem" }}>
              <Form.Label>Tax Amount (per person)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter amount"
                className={
                  (invalidScheduleProps as any).taxAmount && "border-error-2"
                }
                value={
                  schedule.taxAmount !== undefined
                    ? addSeperator(schedule.taxAmount.toString())
                    : ""
                }
                onChange={(e) => {
                  const raw = removeNonNumeric(e.currentTarget.value);
                  onScheduleFormChange(
                    "taxAmount",
                    raw === "" ? undefined : parseInt(raw),
                  );
                }}
              />
              <Form.Text className="text-error-2">
                {(invalidScheduleProps as any).taxAmount}
              </Form.Text>
            </Form.Group>
          )}
          <Form.Check
            type="switch"
            label="Outbound Surcharge"
            className="mb-2"
            checked={schedule.surchargeAmount !== null}
            onChange={() =>
              onScheduleFormChange(
                "surchargeAmount",
                schedule.surchargeAmount !== null ? null : undefined,
              )
            }
          />
          {schedule.surchargeAmount !== null && (
            <Form.Group className="mb-3" style={{ maxWidth: "10rem" }}>
              <Form.Label>Surcharge Amount (per person)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter amount"
                className={
                  (invalidScheduleProps as any).surchargeAmount &&
                  "border-error-2"
                }
                value={
                  schedule.surchargeAmount !== undefined
                    ? addSeperator(schedule.surchargeAmount.toString())
                    : ""
                }
                onChange={(e) => {
                  const raw = removeNonNumeric(e.currentTarget.value);
                  onScheduleFormChange(
                    "surchargeAmount",
                    raw === "" ? undefined : parseInt(raw),
                  );
                }}
              />
              <Form.Text className="text-error-2">
                {(invalidScheduleProps as any).surchargeAmount}
              </Form.Text>
            </Form.Group>
          )}
          {options.hasTwoWay && (
            <>
              <Form.Check
                type="switch"
                label="Return Tax"
                className="mb-2"
                checked={schedule.returnTaxAmount !== null}
                onChange={() =>
                  onScheduleFormChange(
                    "returnTaxAmount",
                    schedule.returnTaxAmount !== null ? null : undefined,
                  )
                }
              />
              {schedule.returnTaxAmount !== null && (
                <Form.Group className="mb-3" style={{ maxWidth: "10rem" }}>
                  <Form.Label>Return Tax Amount (per person)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter amount"
                    className={
                      (invalidScheduleProps as any).returnTaxAmount &&
                      "border-error-2"
                    }
                    value={
                      schedule.returnTaxAmount !== undefined
                        ? addSeperator(schedule.returnTaxAmount.toString())
                        : ""
                    }
                    onChange={(e) => {
                      const raw = removeNonNumeric(e.currentTarget.value);
                      onScheduleFormChange(
                        "returnTaxAmount",
                        raw === "" ? undefined : parseInt(raw),
                      );
                    }}
                  />
                  <Form.Text className="text-error-2">
                    {(invalidScheduleProps as any).returnTaxAmount}
                  </Form.Text>
                </Form.Group>
              )}
              <Form.Check
                type="switch"
                label="Return Surcharge"
                className="mb-2"
                checked={schedule.returnSurchargeAmount !== null}
                onChange={() =>
                  onScheduleFormChange(
                    "returnSurchargeAmount",
                    schedule.returnSurchargeAmount !== null ? null : undefined,
                  )
                }
              />
              {schedule.returnSurchargeAmount !== null && (
                <Form.Group className="mb-3" style={{ maxWidth: "10rem" }}>
                  <Form.Label>Return Surcharge Amount (per person)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter amount"
                    className={
                      (invalidScheduleProps as any).returnSurchargeAmount &&
                      "border-error-2"
                    }
                    value={
                      schedule.returnSurchargeAmount !== undefined
                        ? addSeperator(
                            schedule.returnSurchargeAmount.toString(),
                          )
                        : ""
                    }
                    onChange={(e) => {
                      const raw = removeNonNumeric(e.currentTarget.value);
                      onScheduleFormChange(
                        "returnSurchargeAmount",
                        raw === "" ? undefined : parseInt(raw),
                      );
                    }}
                  />
                  <Form.Text className="text-error-2">
                    {(invalidScheduleProps as any).returnSurchargeAmount}
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
          {options.hasRestrictedReturn && (
            <>
              <Form.Check
                type="switch"
                label="Restricted Return Tax"
                className="mb-2"
                checked={schedule.restrictedReturnTaxAmount !== null}
                onChange={() =>
                  onScheduleFormChange(
                    "restrictedReturnTaxAmount",
                    schedule.restrictedReturnTaxAmount !== null ? null : undefined,
                  )
                }
              />
              {schedule.restrictedReturnTaxAmount !== null && (
                <Form.Group className="mb-3" style={{ maxWidth: "10rem" }}>
                  <Form.Label>Restricted Return Tax Amount (per person)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter amount"
                    className={
                      (invalidScheduleProps as any).restrictedReturnTaxAmount &&
                      "border-error-2"
                    }
                    value={
                      schedule.restrictedReturnTaxAmount !== undefined
                        ? addSeperator(schedule.restrictedReturnTaxAmount.toString())
                        : ""
                    }
                    onChange={(e) => {
                      const raw = removeNonNumeric(e.currentTarget.value);
                      onScheduleFormChange(
                        "restrictedReturnTaxAmount",
                        raw === "" ? undefined : parseInt(raw),
                      );
                    }}
                  />
                  <Form.Text className="text-error-2">
                    {(invalidScheduleProps as any).restrictedReturnTaxAmount}
                  </Form.Text>
                </Form.Group>
              )}
              <Form.Check
                type="switch"
                label="Restricted Return Surcharge"
                className="mb-2"
                checked={schedule.restrictedReturnSurchargeAmount !== null}
                onChange={() =>
                  onScheduleFormChange(
                    "restrictedReturnSurchargeAmount",
                    schedule.restrictedReturnSurchargeAmount !== null ? null : undefined,
                  )
                }
              />
              {schedule.restrictedReturnSurchargeAmount !== null && (
                <Form.Group className="mb-3" style={{ maxWidth: "10rem" }}>
                  <Form.Label>Restricted Return Surcharge Amount (per person)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter amount"
                    className={
                      (invalidScheduleProps as any).restrictedReturnSurchargeAmount &&
                      "border-error-2"
                    }
                    value={
                      schedule.restrictedReturnSurchargeAmount !== undefined
                        ? addSeperator(schedule.restrictedReturnSurchargeAmount.toString())
                        : ""
                    }
                    onChange={(e) => {
                      const raw = removeNonNumeric(e.currentTarget.value);
                      onScheduleFormChange(
                        "restrictedReturnSurchargeAmount",
                        raw === "" ? undefined : parseInt(raw),
                      );
                    }}
                  />
                  <Form.Text className="text-error-2">
                    {(invalidScheduleProps as any).restrictedReturnSurchargeAmount}
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
        </div>
        <div className="d-flex flex-row">
          <h3 className="text-decoration-underline mb-3">Rates</h3>
        </div>
        <Form.Check
          type="switch"
          label="Difer Foreigner"
          className="mb-3"
          checked={options.diferForeigner}
          onChange={() => OnSwitchChanged("difer-foreigner")}
        />
        <Form.Check
          type="switch"
          label="Has Two Way"
          className="mb-3"
          checked={options.hasTwoWay}
          onChange={() => OnSwitchChanged("has-two-way")}
        />
        <Form.Check
          type="switch"
          label="Restricted Return"
          className="mb-3"
          checked={options.hasRestrictedReturn}
          onChange={() => OnSwitchChanged("has-restricted-return")}
        />
        {options.hasRestrictedReturn && (
          <div className="ms-4 mb-3">
            <Form.Group className="mb-3">
              <Form.Label>Valid return providers</Form.Label>
              <AsyncSelect
                isSearchable={true}
                isMulti={true}
                components={animatedComponents}
                menuPlacement="auto"
                placeholder="Select providers"
                getOptionLabel={(p: ScheduleProvider) => p.name}
                getOptionValue={(p: ScheduleProvider) => p._id ?? ""}
                value={schedule.restrictedReturnProviders ?? []}
                isClearable
                noOptionsMessage={() => "No Provider"}
                isOptionSelected={(option) =>
                  !!(schedule.restrictedReturnProviders ?? []).find(
                    (p) => p._id === option._id
                  )
                }
                onChange={(selected) => {
                  onScheduleFormChange(
                    "restrictedReturnProviders",
                    selected ? [...selected] : []
                  );
                }}
                loadOptions={(inputValue, callback) => {
                  getProviders(inputValue, true, 1, 10)
                    .then((res) => callback(res.data.data))
                    .catch(() => callback([]));
                }}
                defaultOptions
              />
              {(invalidScheduleProps as any).restrictedReturnProviders && (
                <Form.Text className="text-error-2">
                  {(invalidScheduleProps as any).restrictedReturnProviders}
                </Form.Text>
              )}
            </Form.Group>
          </div>
        )}
        <Form.Check
          type="switch"
          label="Has Agent Price"
          className="mb-3"
          checked={options.hasAgentPrice}
          onChange={() => OnSwitchChanged("has-agent-price")}
        />
        <Rates
          sources={(schedule.rates ?? []).map((rate, index) => ({
            rate,
            error: {
              basePrice:
                invalidScheduleProps.rates?.length > index
                  ? (invalidScheduleProps.rates[index] as any)?.basePrice ?? ""
                  : "",
            },
          }))}
          onChange={onRateChanges}
          outboundTax={schedule.taxAmount ?? null}
          outboundSurcharge={schedule.surchargeAmount ?? null}
          returnTax={schedule.returnTaxAmount ?? null}
          returnSurcharge={schedule.returnSurchargeAmount ?? null}
          hasRestrictedReturn={options.hasRestrictedReturn}
          restrictedReturnTax={schedule.restrictedReturnTaxAmount ?? null}
          restrictedReturnSurcharge={schedule.restrictedReturnSurchargeAmount ?? null}
        />
        <Button
          onClick={() => onScheduleSave()}
          variant="primary"
          className="w-140"
          type="submit"
        >
          Save Changes
        </Button>
      </Form>

      {/* Metro */}
      <Modal
        show={tripProps.show}
        onHide={() =>
          setTripProps((prev) => ({
            ...prev,
            show: false,
          }))
        }
      >
        <Modal.Header closeButton>
          <Modal.Title>Schedule List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <AsyncSelect
              isSearchable={false}
              isMulti={true}
              components={animatedComponents}
              menuPlacement="auto"
              placeholder="Select Schedule"
              getOptionLabel={(trip: Trip) => trip.date + " - " + trip.time}
              getOptionValue={(trip: Trip) => trip.tripId ?? ""}
              defaultInputValue={undefined}
              defaultValue={tripProps.trip}
              isClearable
              blurInputOnSelect
              noOptionsMessage={() => "No Schedule"}
              isOptionSelected={(option) => {
                if (tripProps.tempTrip) {
                  return !!tripProps.tempTrip?.find(
                    (dt) => dt.tripId == option.tripId,
                  );
                } else {
                  return !!tripProps.trip?.find(
                    (dt) => dt.tripId == option.tripId,
                  );
                }
              }}
              onChange={(trips: MultiValue<Trip>) => {
                let selectedTrips = trips.map((dt) => dt);
                if (selectedTrips) {
                  setTripProps((prev) => ({
                    ...prev,
                    tempTrip: selectedTrips,
                  }));
                } else {
                  setTripProps((prev) => ({ ...prev, tempTrip: undefined }));
                }
              }}
              loadOptions={loadOptions}
              cacheOptions
              defaultOptions
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setTripProps((prev) => ({
                ...prev,
                show: false,
                tempTrip: undefined,
              }))
            }
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={onTripSelect}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Price warning dialog */}
      <Modal show={priceWarning !== null} onHide={() => setPriceWarning(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Restricted return price is not cheaper</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "55vh", overflowY: "auto" }}>
          <p className="mb-3">
            The following prices are not lower than the general return price:
          </p>
          {(priceWarning ?? []).map((item, i) => {
            const showType = (priceWarning ?? []).some(
              (w) => w.rateType === RateType.AGENT
            );
            const typeLabel = item.rateType === RateType.AGENT ? "Agent" : "User";
            const label =
              item.category.charAt(0).toUpperCase() + item.category.slice(1) +
              " (" + (item.isForeigner ? "Foreigner" : "Local") + ")" +
              (showType ? " — " + typeLabel : "");
            const fmtFee = (v: number | null | undefined, name: string) =>
              `${name}: ${v == null ? "—" : `Rp ${addSeperator((v ?? 0).toString())}`}`;
            const outboundSummary = [
              fmtFee(schedule.taxAmount, "Tax"),
              fmtFee(schedule.surchargeAmount, "Surcharge"),
            ].join(" · ");
            const generalReturnSummary = [
              fmtFee(schedule.returnTaxAmount, "Return tax"),
              fmtFee(schedule.returnSurchargeAmount, "Return surcharge"),
            ].join(" · ");
            const restrictedReturnSummary = [
              fmtFee(schedule.restrictedReturnTaxAmount, "Return tax"),
              fmtFee(schedule.restrictedReturnSurchargeAmount, "Return surcharge"),
            ].join(" · ");
            return (
              <div key={i} className="mb-3">
                <div className="fw-600 mb-1">{label}</div>
                <div>General: Rp {addSeperator(item.generalTotal.toString())}</div>
                <div className="text-muted mb-1" style={{ fontSize: ".75rem" }}>
                  {outboundSummary} · {generalReturnSummary}
                </div>
                <div>Restricted: Rp {addSeperator(item.restrictedTotal.toString())}</div>
                <div className="text-muted" style={{ fontSize: ".75rem" }}>
                  {outboundSummary} · {restrictedReturnSummary}
                </div>
              </div>
            );
          })}
          <p className="mt-3 mb-1 text-dark">
            Typically restricted return should be cheaper to incentivise provider
            loyalty.
          </p>
          <p className="text-dark">Are you sure you want to save?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPriceWarning(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={executeSave}>
            Yes, save anyway
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ScheduleForm;
